<script setup lang="ts">
type Props = {
  gameDetails?: any;
  isGameModalOpen?: boolean;
  gameSlug: string;
  gameDemoMode: string;
  statistics: GameStatisticsByPeriod | null;
};

const { gameDetails, isGameModalOpen, gameSlug, gameDemoMode } =
  defineProps<Props>();

type Emits = {
  'full-screen-off': [];
};

const emit = defineEmits<Emits>();

const { $containment } = useNuxtApp();
const { t } = useTexts();
const { ApiService } = useApiService();
const { isMobile } = useLayout();

// iOS: em fullscreen o iframe do jogo e fixed e ia ate as bordas — o topo
// ficava sob a barra (agora mais alta pela safe area) e o rodape sob o home
// indicator, cortando o conteudo do jogo (que e cross-origin, nao da pra
// estilizar por dentro). Insetamos o proprio container. Ver .iosGameSafe.
const isIosApp = useIsIosNativeApp();
const { isAuthenticated } = storeToRefs(useAuthStore());
const { openLogin } = useAuth();

const gameIframeElement = ref<HTMLIFrameElement | null>(null);

const { normalizeThumbnail } = useCasino();

const thumbnailPath = computed<string>(() => {
  return `${normalizeThumbnail(gameDetails.image, true)}/public`;
});

const route = useRoute('games-provider-game');

const providerNameFromUrl = computed<string>(() => {
  return route.params.provider;
});

const isLiveG24Provider = computed<boolean>(() => {
  return providerNameFromUrl.value === 'liveg24';
});

const isPGSoftProvider = computed<boolean>(() => {
  return providerNameFromUrl.value === 'pgsoft';
});

const isAltenteProvider = computed<boolean>(() => {
  return (
    providerNameFromUrl.value === 'altente' ||
    providerNameFromUrl.value === 'alg'
  );
});

const isAmusnetProvider = computed<boolean>(() => {
  return providerNameFromUrl.value === 'amusnet';
});

const isEvolutionProvider = computed<boolean>(() => {
  return providerNameFromUrl.value === 'evolution';
});

const isAviatrixProvider = computed<boolean>(() => {
  return providerNameFromUrl.value === 'aviatrix';
});

const is7MojosProvider = computed<boolean>(() => {
  return providerNameFromUrl.value === 'sevenmojos';
});

const isGoTechProvider = computed<boolean>(() => {
  return (
    !!gameIframeDetails.value &&
    !!gameIframeDetails.value.url &&
    !!gameIframeDetails.value.url.includes('gotech')
  );
});

const iframePermissions = computed(() => {
  if (isAviatrixProvider.value) {
    return 'clipboard-write';
  }

  if (isAltenteProvider.value) {
    return 'autoplay; fullscreen';
  }

  if (isAmusnetProvider.value) {
    return 'autoplay; fullscreen; screen-wake-lock';
  }

  if (is7MojosProvider.value) {
    return 'autoplay; fullscreen';
  }

  return '';
});

function requestFullScreen() {
  if (!isMobile.value) {
    return;
  }

  const iframe = gameIframeElement.value;

  if (!iframe) {
    return;
  }

  if (iframe.requestFullscreen) {
    iframe.requestFullscreen().catch((err) => {
      console.error(
        `Error attempting to enable full-screen mode: ${err.message} (${err.name})`,
      );
    });
    // @ts-ignore
  } else if (iframe.webkitRequestFullScreen) {
    // @ts-ignore
    iframe.webkitRequestFullScreen();
    // @ts-ignore
  } else if (iframe.msRequestFullscreen) {
    // @ts-ignore
    iframe.msRequestFullscreen();
  }
}

const { goToLocaleRoute } = useRoutes();

async function goToProviderGamesPage() {
  await goToLocaleRoute({
    name: 'games-providers-provider',
    params: {
      provider: providerNameFromUrl.value,
    },
  });
}

function listenLiveG24({ data }: MessageEvent) {
  if (data === 'closeGame') {
    return goToProviderGamesPage();
  }
}

function listenAmusnet({ data }: MessageEvent) {
  if (!data) {
    return;
  }

  if (data.command === 'com.egt-bg.exit') {
    return goToProviderGamesPage();
  }

  if (data.command === 'com.egt-bg.reload') {
    location.reload();
  }
}

function onMessagesListener(message: MessageEvent) {
  if (isLiveG24Provider.value) {
    listenLiveG24(message);
  }

  if (isAmusnetProvider.value) {
    listenAmusnet(message);
  }
}

function onIframeLoad() {
  if (isAltenteProvider.value) {
    requestFullScreen();
  }
}

interface GameIframeDetails {
  url?: string;
  launchOptions?: any;
  isLoading: boolean;
  isFetching: boolean;
  hasError: boolean;
  errorMessage: string;
}

const gameIframeDetails = ref<GameIframeDetails>({
  isLoading: true,
  isFetching: false,
  hasError: false,
  errorMessage: '',
});

function resetGameIframeDetails() {
  gameIframeDetails.value.url = '';
  gameIframeDetails.value.launchOptions = null;
  gameIframeDetails.value.hasError = false;
  gameIframeDetails.value.isLoading = true;
  gameIframeDetails.value.isFetching = false;
  gameIframeDetails.value.errorMessage = '';
}

function setGameIframeError(message: string) {
  gameIframeDetails.value.hasError = true;
  gameIframeDetails.value.errorMessage = message;
  gameIframeDetails.value.isLoading = false;
  gameIframeDetails.value.isFetching = false;
}

const iframeBinds = computed<Record<string, any>>(() => {
  const binds: Record<string, any> = {
    id: 'gameIframe',
    allow: iframePermissions.value,
    frameborder: '0',
    height: '100%',
    title: `${gameDetails.name} Iframe`,
    width: '100%',
  };

  if (isPGSoftProvider.value && !isGoTechProvider.value) {
    binds.srcdoc = gameIframeDetails.value.url;
  } else {
    binds.src = gameIframeDetails.value.url;
  }

  if (isAltenteProvider.value || is7MojosProvider.value) {
    binds.allowfullscreen = true;
  }

  return binds;
});

const isDefaultIframeActive = computed(() => {
  return (
    !gameIframeDetails.value.launchOptions &&
    gameIframeDetails.value.url &&
    !gameIframeDetails.value.hasError
  );
});

const isAnyDataLoading = computed(() => {
  return gameDetails.isLoading || gameIframeDetails.value.isLoading;
});

async function loadSoftswissGame() {
  if (!gameIframeDetails.value.launchOptions) {
    return;
  }

  const { game_url, mobile_url, desktop_url } =
    gameIframeDetails.value.launchOptions;

  const gameUrl = game_url || (isMobile.value ? mobile_url : desktop_url);

  if (!gameUrl) {
    return;
  }

  const GAME_WRAPPER_ID = 'game-wrapper';

  await sleep(200);

  // Garantimos que o iframe não seja duplicado
  const softSwissGameWrapper = document.getElementById(GAME_WRAPPER_ID);

  if (softSwissGameWrapper) {
    softSwissGameWrapper.innerHTML = '';
  }

  const gameLaunchOptions: any = { target_element: GAME_WRAPPER_ID };

  gameLaunchOptions.launch_options = {
    game_url: gameUrl,
    strategy: 'iframe',
  };

  const onSuccess = (success: any) => {
    console.log('✅ onSuccessLaunch: ', success);
  };

  const onError = (error: any) => {
    console.error('❌ onErrorLaunch: ', error);
  };

  await window.sg.launch(gameLaunchOptions, onSuccess, onError);
}

async function loadGameIframe(source: string) {
  if (!gameSlug) {
    return;
  }

  if (gameIframeDetails.value.isFetching) {
    return;
  }

  resetGameIframeDetails();

  if (!isAuthenticated.value) {
    gameIframeDetails.value.url = '';
    gameIframeDetails.value.launchOptions = null;
    setGameIframeError(t('casino.game.login_error'));

    return;
  }

  const { isValidationModuleEnabled } = useModulesValidation();

  if (isValidationModuleEnabled('casino')) {
    await $containment.fetchCurrentPending('casino');

    if ($containment.hasPendencyOn('casino')) {
      const { $localePath } = useNuxtApp();
      const localePath = $localePath as ((p: string) => string) | undefined;

      $containment.closeVerificationModal();

      return await navigateTo(localePath ? localePath('/') : '/');
    }
  }

  const useDemo: number = gameDemoMode === 'demo' ? 1 : 0;
  const platform = isMobile.value ? 'MOBILE' : 'WEB';

  gameIframeDetails.value.isLoading = true;
  gameIframeDetails.value.isFetching = true;

  const params = new URLSearchParams({
    slug: gameSlug,
    platform,
    use_demo: String(useDemo),
    source,
    tab_id: getTabId(),
    mounted_id: getMountId(),
  });

  await ApiService.get(`/start-game-v2?${params}`)
    .then(async ({ data }) => {
      if (data.launchOptions) {
        if (data.launchOptions.launch_options) {
          gameIframeDetails.value.launchOptions =
            data.launchOptions.launch_options;
        } else {
          gameIframeDetails.value.launchOptions = data.launchOptions;
        }
      } else if (data.gameURL) {
        gameIframeDetails.value.url = data.gameURL;
      } else if (data.url) {
        gameIframeDetails.value.url = data.url;
      } else {
        setGameIframeError(t(`error.game_not_found`));
      }

      await loadSoftswissGame();
    })
    .catch((err) => {
      if (err.message && err.message.includes('401')) {
        setGameIframeError(
          err.message || err.description || t('casino.game.login_error'),
        );
      } else {
        setGameIframeError(
          err.message || err.description || t(`error.game_not_found`),
        );
      }
    })
    .finally(() => {
      gameIframeDetails.value.isLoading = false;
      gameIframeDetails.value.isFetching = false;
    });
}

onMounted(() => {
  window.addEventListener('message', onMessagesListener, { passive: true });
});

onBeforeRouteLeave(() => {
  window.removeEventListener('message', onMessagesListener);
});

watch(
  () => isAuthenticated.value,
  async () => {
    await loadGameIframe('watchIsAuthenticated');
  },
  {
    immediate: true,
  },
);

watch(
  () => gameDemoMode,
  (newValue, oldValue) => {
    if (newValue !== oldValue) {
      setTimeout(async () => {
        await loadGameIframe('watchGameDemoMode');
      }, 300);
    }
  },
);
</script>

<template>
  <section
    :key="isEvolutionProvider ? 0 : isMobile ? 1 : 0"
    :class="[
      $style.frameGameSection,
      { [$style.isFullScreen]: isGameModalOpen },
      { [$style.iosGameSafe]: isIosApp },
    ]"
  >
    <CasinoGamesBar @full-screen-off="emit('full-screen-off')" />

    <div
      v-if="isAnyDataLoading"
      :class="$style.loadingSection"
    >
      <BaseLoading
        class="!bg-transparent !absolute"
        :show="true"
      />
    </div>

    <div
      v-if="gameIframeDetails.hasError && !isAnyDataLoading"
      :class="$style.errorSection"
    >
      <div
        v-if="!isAuthenticated"
        :class="$style.authError"
      >
        <div :class="$style.previewStack">
          <div :class="$style.previewThumbWrapper">
            <div :class="$style.previewThumb">
              <CasinoMediaApostatudo
                v-if="gameDetails.image"
                :game="gameDetails"
                no-link
              />

              <NuxtIcon
                v-else
                class="text-4xl"
                name="exclamation"
              />
            </div>
          </div>

          <div :class="$style.previewCard">
            <header :class="$style.previewTopbar">
              <CasinoGameActionsMenu
                :game="gameDetails"
                share-only
              />
            </header>

            <div :class="$style.previewTitleBlock">
              <h2 :class="$style.previewName">{{ gameDetails.name }}</h2>
              <p :class="$style.previewProvider">
                {{
                  typeof gameDetails.provider === 'string'
                    ? gameDetails.provider
                    : gameDetails.provider?.name || ''
                }}
              </p>
            </div>

            <button
              :class="$style.previewPlayBtn"
              type="button"
              @click="openLogin()"
            >
              <svg
                :class="$style.previewPlayIcon"
                fill="none"
                height="13"
                viewBox="0 0 11 13"
                width="11"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M11 6.5L0 0V13L11 6.5Z"
                  fill="currentColor"
                />
              </svg>
              JOGAR
            </button>

            <CasinoGameTagChips
              v-if="gameDetails.tags?.length"
              :class="$style.previewChips"
              :reset-key="gameDetails.id"
              :tags="gameDetails.tags"
            />
          </div>
        </div>
      </div>

      <div
        v-else
        :class="$style.genericError"
      >
        <NuxtIcon
          class="text-4xl"
          name="exclamation"
        />

        <span>{{ gameIframeDetails.errorMessage }}</span>
      </div>
    </div>

    <div
      id="game-wrapper"
      :class="$style.gameWrapper"
    >
      <template v-if="isDefaultIframeActive">
        <iframe
          ref="gameIframeElement"
          v-bind="iframeBinds"
          @load.once="onIframeLoad"
        ></iframe>
      </template>
    </div>

    <div
      v-if="gameIframeDetails.hasError && !gameIframeDetails.isLoading"
      :class="$style.frameBg"
      :style="`background-image:url(${thumbnailPath})`"
    />
  </section>
</template>

<style module lang="postcss">
.frameGameSection {
  @apply relative flex flex-col w-full h-[44vh] lg:h-[70vh] bg-black border border-solid border-white/10 rounded-t-lg overflow-hidden;

  @media (max-width: 767px) {
    &:not(.isFullScreen) {
      @apply hidden;
    }
  }

  iframe {
    @apply w-full h-full relative z-10 overflow-hidden;
  }

  .loadingSection {
    @apply w-full h-full text-center flex items-center justify-center absolute z-10;

    svg {
      @apply w-12 h-12 text-primary;
    }
  }

  .errorSection {
    @apply absolute z-20 w-full h-full flex flex-col items-center justify-center gap-2.5 text-center font-bold text-sm md:text-base;

    svg {
      @apply w-2 h-2 text-3xl;
    }

    .authError {
      @apply flex flex-col items-center justify-center gap-6 text-white;

      .authErrorImage {
        @apply rounded-lg border-4 border-solid border-white/10 shadow-lg shadow-black/20 overflow-hidden;
        width: 192px;
        height: 224px;
        flex-shrink: 0;
      }

      .previewStack {
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: stretch;
        width: 320px;
        max-width: calc(100% - 32px);
      }

      .previewThumbWrapper {
        position: relative;
        z-index: 11;
        display: flex;
        justify-content: center;
        margin-bottom: -50px;
        pointer-events: none;
      }

      .previewThumb {
        width: 110px;
        aspect-ratio: 6 / 7;
        border-radius: 12px;
        position: relative;
        pointer-events: auto;

        :global(img) {
          border-radius: 12px;
        }
      }

      .previewCard {
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: stretch;
        padding: 66px 16px 16px;
        border-radius: 18px;
        background: linear-gradient(180deg, #0a0f2e 0%, #020716 100%);
        border: 1px solid rgba(255, 255, 255, 0.08);
        box-shadow: 0 12px 40px rgba(0, 0, 0, 0.5);
      }

      .previewTopbar {
        position: absolute;
        top: 12px;
        left: 12px;
        right: 12px;
        display: flex;
        justify-content: flex-start;
        align-items: center;
        z-index: 20;
      }

      .previewTitleBlock {
        text-align: center;
        padding: 10px 4px;
        user-select: none;
        -webkit-user-select: none;
      }

      .previewName {
        color: #ffffff;
        font-size: 20px;
        font-weight: 700;
        line-height: 1.2;
        margin: 0 0 4px;
      }

      .previewProvider {
        color: #98a2b3;
        font-size: 13px;
        font-weight: 500;
        margin: 0;
      }

      .previewPlayBtn {
        position: relative;
        overflow: hidden;
        display: flex;
        width: 100%;
        height: 35px;
        padding: 0 10px;
        justify-content: center;
        align-items: center;
        gap: 5px;
        flex-shrink: 0;
        border-radius: 6px;
        border: 1px solid #fc9984;
        background: radial-gradient(
          168.3% 138% at 36.76% -16.67%,
          #ff704a 0%,
          #ff3400 100%
        );
        color: #ffffff;
        text-align: center;
        font-family: 'Plus Jakarta Sans', sans-serif;
        font-size: 11px;
        font-weight: 600;
        line-height: 12px;
        cursor: pointer;
        transition:
          filter 0.15s ease,
          transform 0.1s ease;
      }

      .previewPlayBtn::before {
        content: '';
        position: absolute;
        top: -50%;
        left: -100%;
        width: 50%;
        height: 200%;
        background: linear-gradient(
          90deg,
          transparent 0%,
          rgba(255, 255, 255, 0.5) 50%,
          transparent 100%
        );
        transform: skewX(-25deg);
        animation: previewShimmerSlide 1.5s ease-out infinite;
        pointer-events: none;
        z-index: 1;
      }

      .previewPlayBtn > * {
        position: relative;
        z-index: 2;
      }

      @keyframes previewShimmerSlide {
        0% {
          left: -100%;
        }
        100% {
          left: 150%;
        }
      }

      .previewPlayBtn:hover {
        filter: brightness(1.08);
      }

      .previewPlayBtn:active {
        transform: scale(0.98);
      }

      .previewPlayIcon {
        width: 11px;
        height: 13px;
        flex-shrink: 0;
        display: block;
      }

      .previewChips {
        margin-top: 16px;
      }
    }

    .genericError {
      @apply flex flex-col items-center justify-center gap-2.5 text-error;
    }

    .btnCta {
      @apply flex justify-center items-center gap-1.5 py-2.5 px-6 font-bold text-base rounded-full;

      svg {
        @apply w-4 h-4 text-button-text;
      }
    }
  }

  &.isFullScreen {
    @apply w-full fixed top-0 left-0 z-[999];
    height: calc(100% - 40px);
    margin-top: 40px;
  }

  /* iOS app: a barra do jogo (games/Bar.vue) fica mais alta pela safe area
     do topo (40px + max(env-top, --ios-safe-top-min)); o iframe precisa
     comecar abaixo dela e terminar acima do home indicator (env-bottom),
     senao o conteudo do jogo (cross-origin) fica cortado nas duas pontas. */
  &.isFullScreen.iosGameSafe {
    /* Ancora do topo (abaixo da barra) ate a borda inferior fisica: a section
       (bg preto) cobre a area toda, inclusive o home indicator — senao a home
       por tras vaza embaixo. O iframe e insetado por dentro via padding pra
       nao ficar sob a barra (topo) nem sob o home indicator (rodape). */
    top: calc(
      40px + max(env(safe-area-inset-top, 0px), var(--ios-safe-top-min))
    );
    bottom: 0;
    height: auto;
    margin-top: 0;
    /* Rodape pequeno (nao reserva o home indicator inteiro): sem barra
       inferior pra preencher, o env-bottom viraria uma faixa preta grande.
       Usa o piso pequeno e deixa o home indicator flutuar sobre o conteudo,
       igual a sensacao da home. */
    padding-bottom: var(--ios-safe-bottom-min);
  }

  .frameBg {
    @apply absolute top-0 left-0 z-0 w-full h-full bg-cover bg-top blur-md opacity-20 transform scale-125;
  }
}

.gameWrapper {
  @apply h-full md:mt-0;

  iframe {
    width: 100% !important;
    height: 100% !important;
    border: 0 !important;
  }
}
</style>
